import React, { useState, useEffect, useRef } from "react";
import API from "../api/index";
import { unlockExpiry, rememberUnlock } from './password-session';

const PasswordPrompt = ({ onSubmit }) => {
	const [password, setPassword] = useState("");
	const [displayChar, setDisplayChar] = useState("");
	const timeoutRef = useRef(null);

	const handleSubmit = (e) => {
		e.preventDefault();
		onSubmit(password);
	};

	const handleKeyPress = (e) => {
		if (e.key === "Enter") {
			handleSubmit(e);
		}
	};

	const handleInputChange = (e) => {
		const inputValue = e.target.value;
		const newChar = inputValue.slice(-1); // Get the last character

		setPassword(inputValue);
		setDisplayChar(newChar);

		// Clear the previous timeout if it exists
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		// Set a new timeout to hide the character after 0.5 seconds
		timeoutRef.current = setTimeout(() => {
			setDisplayChar("•");
		}, 150);
	};

	return (
		<div className="password-prompt-modal">
			<form onSubmit={handleSubmit}>
				<div style={{ display: "flex", alignItems: "center" }}>
					<span style={{ marginRight: "8px", minWidth: "20px", textAlign: "center",fontFamily:"Arial" }}>{displayChar}</span>
					<input
						type="text"
						style={{ textAlign: "center", WebkitTextSecurity: "disc" }}
						placeholder="passkey"
						value={password}
						onChange={handleInputChange}
						onKeyPress={handleKeyPress}
						autoComplete="off"
						name="passkey"
						id="passkey"
					/>
				</div>
			</form>
		</div>
	);
};

const withPasswordProtection = (apiPath, timeoutMinutes) => (WrappedComponent) => {
	return (props) => {
		const identity = () => localStorage.getItem('user_hash') || '';
		const cachedExpiry = () => { try { return unlockExpiry(localStorage, apiPath, identity()); } catch (_) { return 0; } };
		const [until, setUntil] = useState(cachedExpiry);
		const [busy, setBusy] = useState(false);

		const validatePassword = async (password) => {
			if (busy) return;
			setBusy(true);
			const login = identity();
			return API.post(apiPath, { password })
				.then((data) => {
					if (!data || login !== identity()) return false;
					if (data.RELOGIN) {
						data.RELOGIN ? props.logoutUser(props.history) : 0;
						return false;
					} else if (!data.ERRORS?.length) {
						setUntil(rememberUnlock(localStorage, apiPath, login, timeoutMinutes));
						return true;
					}
				})
				.catch((error) => {
					return false;
					}).finally(() => setBusy(false));
		};

		const handlePasswordSubmit = async (password) => {
			await validatePassword(password);
		};

		useEffect(() => {
			const timer = setTimeout(() => setUntil(0), Math.max(0, until - Date.now()));
			const check = () => setUntil(cachedExpiry());
			window.addEventListener('focus', check);
			window.addEventListener('storage', check);
			document.addEventListener('visibilitychange', check);
			return () => { clearTimeout(timer); window.removeEventListener('focus', check); window.removeEventListener('storage', check); document.removeEventListener('visibilitychange', check); };
		}, [until]);

		return until > Date.now() ? <WrappedComponent {...props} /> : <PasswordPrompt onSubmit={handlePasswordSubmit} />;
	};
};

export default withPasswordProtection;
